// ============================================================================
// ThresholdGauge — a horizontal "where do I sit" gauge for a body ratio.
// ----------------------------------------------------------------------------
// Renders a lime→amber→coral track with the risk threshold(s) marked, and an
// animated pin that slides to the latest value. Communicates "within range" vs
// "above threshold" at a glance. Reduced-motion aware.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { clamp, fmtNum } from "../format";

export interface ThresholdGaugeProps {
  value: number;
  /** Primary risk threshold (e.g. WHR 0.85 / WHtR 0.5). */
  threshold: number;
  /** Optional upper "high risk" band (WHtR 0.6). */
  highThreshold?: number;
  decimals?: number;
  /** Whether the value sits within the healthy range. */
  withinRange: boolean;
  lowLabel?: string;
  highLabel?: string;
  ariaLabel?: string;
  locale?: string;
}

/** A horizontal threshold gauge with an animated value pin. */
export function ThresholdGauge({
  value,
  threshold,
  highThreshold,
  decimals = 2,
  withinRange,
  lowLabel,
  highLabel,
  ariaLabel,
  locale,
}: ThresholdGaugeProps) {
  const reduce = useReducedMotion();

  // Scale: 0 → 1.5× the highest reference, so the threshold sits ~⅔ across and
  // the pin always lands on-track for realistic body ratios.
  const top = Math.max(highThreshold ?? threshold, value) * 1.18;
  const bottom = 0;
  const span = top - bottom || 1;

  const toPct = (v: number) => clamp(((v - bottom) / span) * 100, 0, 100);
  const valuePct = toPct(value);
  const thrPct = toPct(threshold);
  const highPct = highThreshold !== undefined ? toPct(highThreshold) : undefined;

  return (
    <div className="w-full" role="img" aria-label={ariaLabel}>
      <div className="relative h-3 w-full overflow-hidden rounded-pill bg-surface-sunken">
        {/* Tri-band track */}
        <div
          className="absolute inset-0 rounded-pill"
          style={{
            background:
              "linear-gradient(90deg, #18C964 0%, #3BE085 45%, #F5A524 70%, #FF8A3D 85%, #FF5E62 100%)",
            opacity: 0.85,
          }}
        />
        {/* Threshold marker(s) */}
        <span
          aria-hidden
          className="absolute top-[-3px] bottom-[-3px] w-0.5 bg-ink/55"
          style={{ left: `${thrPct}%` }}
        />
        {highPct !== undefined && (
          <span
            aria-hidden
            className="absolute top-[-3px] bottom-[-3px] w-0.5 bg-ink/40"
            style={{ left: `${highPct}%` }}
          />
        )}
      </div>

      {/* Animated value pin */}
      <div className="relative h-6">
        <motion.div
          className="absolute top-0 -translate-x-1/2"
          initial={reduce ? { left: `${valuePct}%` } : { left: "0%" }}
          whileInView={{ left: `${valuePct}%` }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: reduce ? 0 : 0.9, ease: [0.22, 0.61, 0.36, 1] }}
        >
          <span
            className={
              "grid -translate-y-1 place-items-center rounded-pill px-2 py-0.5 text-[11px] font-extrabold text-white shadow-sm " +
              (withinRange ? "bg-grad-lime" : "bg-grad-coral")
            }
          >
            {fmtNum(value, decimals, locale)}
          </span>
          <span
            aria-hidden
            className={
              "mx-auto mt-0.5 block h-2 w-2 rotate-45 " +
              (withinRange ? "bg-lime" : "bg-coral")
            }
          />
        </motion.div>
      </div>

      {(lowLabel || highLabel) && (
        <div className="mt-0.5 flex justify-between text-[10px] font-bold uppercase tracking-kicker text-body-faint">
          <span>{lowLabel}</span>
          <span>{highLabel}</span>
        </div>
      )}
    </div>
  );
}
